import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Wallet, CheckCircle2, Clock } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/* What a cleaner or vendor is owed for their own work. The server only ever returns this
   person's payouts, never the company's payroll. */
export default function MyPay() {
  const [payouts, setPayouts] = useState(null);

  useEffect(() => {
    api.get("/payouts").then(({ data }) => setPayouts(data)).catch(() => setPayouts([]));
  }, []);

  if (!payouts) {
    return <div className="py-20 text-center" data-testid="my-pay-loading">
      <Loader2 className="mx-auto h-6 w-6 animate-spin text-royal" />
    </div>;
  }

  const pending = payouts.filter((p) => p.status !== "paid");
  const owed = pending.reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="my-pay-page">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">Your work</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-royal-deep">My Pay</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every clean you have completed and what it pays.
        </p>
      </div>

      <div className="rounded-lg border border-gold/40 bg-gold/5 p-5" data-testid="my-pay-summary">
        <p className="text-xs uppercase tracking-[0.16em] text-royal/70">Awaiting payment</p>
        <p className="font-display text-3xl font-bold text-royal-deep">{money(owed)}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {pending.length} payout{pending.length === 1 ? "" : "s"} pending
        </p>
      </div>

      {!payouts.length ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center"
             data-testid="my-pay-empty">
          <Wallet className="mx-auto h-7 w-7 text-royal/40" />
          <p className="mt-3 text-sm text-muted-foreground">
            Nothing here yet — your pay appears once your completed work is processed.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {payouts.map((p) => (
            <div key={p._id} data-testid={`my-payout-${p._id}`}
                 className="rounded-lg border border-border bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-royal-deep">
                    {p.period_start} – {p.period_end}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {p.jobs} job{p.jobs === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-display text-xl font-bold text-royal-deep">{money(p.amount)}</p>
                  <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider
                    ${p.status === "paid" ? "text-emerald-700" : "text-amber-700"}`}>
                    {p.status === "paid"
                      ? <><CheckCircle2 className="h-3 w-3" /> Paid</>
                      : <><Clock className="h-3 w-3" /> {p.status}</>}
                  </span>
                </div>
              </div>
              <div className="mt-3 divide-y divide-border border-t border-border">
                {(p.items || []).map((it) => (
                  <div key={it.work_order_id}
                       className="flex items-center justify-between gap-3 py-2 text-xs">
                    <span className="truncate text-royal-deep">{it.title}</span>
                    <span className="shrink-0 text-muted-foreground">
                      {it.date} · {money(it.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
